import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";

const API_BASE = config.API_BASE_URL;

export default function GaugeManagementPage() {
  const [gauges, setGauges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // --- Main Gauge Modal State ---
  const [showModal, setShowModal] = useState(false);
  const [editingGauge, setEditingGauge] = useState(null);
  const [formData, setFormData] = useState({
    gauge_type: "Calipers",
    gauge_name: "",
    serial_number: "",
    manufacturer: "",
    location: "Quality Lab",
    calibration_status: "Active",
    last_calibration_date: "",
    calibration_frequency_days: 365,
    is_master_standard: false,
    notes: ""
  });

  // --- Calibration History & Entry Modal State ---
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyGauge, setHistoryGauge] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  
  // New Calibration Form Data
  const [newCalData, setNewCalData] = useState({
    calibration_date: new Date().toISOString().split('T')[0],
    performed_by: "Internal Lab",
    result: "Pass",
    standard_gauge_id: "",
    temperature: "68°F",
    notes: ""
  });
  
  // Measurement Data Rows
  const [measurements, setMeasurements] = useState([
    { nominal: 1.000, tolerance: 0.001, actual: "" }
  ]);

  useEffect(() => {
    loadGauges();
  }, []);

  const loadGauges = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/gauges?active_only=true`);
      setGauges(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to load gauge list.");
    } finally {
      setLoading(false);
    }
  };

  // --- Gauge CRUD Operations ---

  const handleEdit = (gauge) => {
    setEditingGauge(gauge);
    setFormData({
      gauge_type: gauge.gauge_type,
      gauge_name: gauge.gauge_name || "",
      serial_number: gauge.serial_number || "",
      manufacturer: gauge.manufacturer || "",
      location: gauge.location || "",
      calibration_status: gauge.calibration_status,
      last_calibration_date: gauge.last_calibration_date || "",
      calibration_frequency_days: gauge.calibration_frequency_days || 365,
      is_master_standard: gauge.is_master_standard || false,
      notes: gauge.notes || ""
    });
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingGauge(null);
    setFormData({
      gauge_type: "Calipers",
      gauge_name: "",
      serial_number: "",
      manufacturer: "",
      location: "Quality Lab",
      calibration_status: "Active",
      last_calibration_date: new Date().toISOString().split('T')[0],
      calibration_frequency_days: 365,
      is_master_standard: false,
      notes: ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this gauge?")) return;
    try {
      await axios.delete(`${API_BASE}/gauges/${id}`);
      loadGauges();
    } catch (err) {
      alert("Failed to delete gauge: " + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const key in formData) {
        payload[key] = formData[key] === "" ? null : formData[key];
    }

    try {
      if (editingGauge) {
        await axios.patch(`${API_BASE}/gauges/${editingGauge.gauge_id}`, payload);
      } else {
        await axios.post(`${API_BASE}/gauges`, payload);
      }
      setShowModal(false);
      loadGauges();
    } catch (err) {
      alert("Error saving gauge: " + err.message);
    }
  };

  // --- Calibration Logic ---

  const handleViewHistory = async (gauge) => {
    setHistoryGauge(gauge);
    setHistoryList([]);
    setMeasurements([{ nominal: 1.000, tolerance: 0.001, actual: "" }]); // Reset measurements
    setNewCalData({
        calibration_date: new Date().toISOString().split('T')[0],
        performed_by: "Internal Lab",
        result: "Pass",
        standard_gauge_id: "",
        temperature: "68°F",
        notes: ""
    });
    setShowHistoryModal(true);
    
    try {
        const res = await axios.get(`${API_BASE}/gauges/${gauge.gauge_id}/calibrations`);
        setHistoryList(res.data);
    } catch (err) {
        alert("Failed to load history: " + err.message);
    }
  };

  // Add/Remove Measurement Rows
  const addMeasurementRow = () => {
    setMeasurements([...measurements, { nominal: 0, tolerance: 0.001, actual: 0 }]);
  };

  const updateMeasurement = (index, field, value) => {
    const updated = [...measurements];
    updated[index][field] = value;
    setMeasurements(updated);
  };

  const removeMeasurementRow = (index) => {
    const updated = measurements.filter((_, i) => i !== index);
    setMeasurements(updated);
  };

  const handleCalibrationSubmit = async (e) => {
    e.preventDefault();
    if(!historyGauge) return;

    // Prepare Payload
    const payload = {
        ...newCalData,
        measurement_data: measurements.map(m => ({
            nominal: parseFloat(m.nominal),
            tolerance: parseFloat(m.tolerance),
            actual: parseFloat(m.actual)
        }))
    };

    try {
        await axios.post(`${API_BASE}/gauges/${historyGauge.gauge_id}/calibrations`, payload);
        
        // Refresh history list
        const res = await axios.get(`${API_BASE}/gauges/${historyGauge.gauge_id}/calibrations`);
        setHistoryList(res.data);
        
        alert("Calibration recorded successfully!");
        // Refresh main list in background
        loadGauges();

    } catch (err) {
        alert("Error adding calibration: " + err.message);
    }
  };

  // --- Helper to Color Code Calibration Status ---
  const getStatusBadge = (gauge) => {
    const today = new Date();
    const nextCal = gauge.next_calibration_date ? new Date(gauge.next_calibration_date) : null;
    
    if (gauge.calibration_status !== 'Active') {
        return <span className="badge bg-secondary">{gauge.calibration_status}</span>;
    }
    if (!nextCal) return <span className="badge bg-info text-dark">Unknown</span>;

    const diffTime = nextCal - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
        return <span className="badge bg-danger">OVERDUE ({Math.abs(diffDays)} days)</span>;
    } else if (diffDays <= 30) {
        return <span className="badge bg-warning text-dark">Due Soon ({diffDays} days)</span>;
    } else {
        return <span className="badge bg-success">OK</span>;
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gauge & Tool Management (PPAP)</h2>
        <button className="btn btn-primary" onClick={handleAddNew}>
          <i className="fas fa-plus me-2"></i>Add New Gauge
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      
      {loading ? (
        <div>Loading tools...</div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body p-0">
            <table className="table table-striped table-hover mb-0 align-middle">
              <thead className="table-dark">
                <tr>
                  <th>Type</th>
                  <th>Name / ID</th>
                  <th>Serial #</th>
                  <th>Location</th>
                  <th>Last Cal.</th>
                  <th>Next Cal.</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gauges.map(g => (
                  <tr key={g.gauge_id}>
                    <td>
                        {g.gauge_type}
                        {g.is_master_standard && <span className="badge bg-primary ms-2">MASTER</span>}
                    </td>
                    <td className="fw-bold">{g.gauge_name}</td>
                    <td>{g.serial_number || '-'}</td>
                    <td>{g.location}</td>
                    <td>{g.last_calibration_date || '-'}</td>
                    <td>{g.next_calibration_date || '-'}</td>
                    <td>{getStatusBadge(g)}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-info me-2" onClick={() => handleViewHistory(g)} title="Calibrate / History">
                        <i className="fas fa-balance-scale"></i> Calibrate
                      </button>
                      <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleEdit(g)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(g.gauge_id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
                {gauges.length === 0 && (
                    <tr><td colSpan="8" className="text-center p-4">No gauges found. Add one to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main Gauge Edit/Create Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingGauge ? "Edit Gauge" : "Add New Gauge"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Gauge Type *</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            list="gaugeTypes"
                            value={formData.gauge_type}
                            onChange={e => setFormData({...formData, gauge_type: e.target.value})}
                            required
                        />
                        <datalist id="gaugeTypes">
                            <option value="Calipers" />
                            <option value="Micrometer" />
                            <option value="Tape Measure" />
                            <option value="Height Gauge" />
                            <option value="Protractor" />
                            <option value="Pin Gauge Set" />
                            <option value="Thread Plug Gauge" />
                            <option value="Gauge Blocks" />
                        </datalist>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Internal Name / ID</label>
                        <input type="text" className="form-control" placeholder="e.g. Caliper-01" value={formData.gauge_name} onChange={e => setFormData({...formData, gauge_name: e.target.value})} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Manufacturer</label>
                        <input type="text" className="form-control" value={formData.manufacturer} onChange={e => setFormData({...formData, manufacturer: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Serial Number</label>
                        <input type="text" className="form-control" value={formData.serial_number} onChange={e => setFormData({...formData, serial_number: e.target.value})} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Location</label>
                        <input type="text" className="form-control" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Status</label>
                        <select className="form-select" value={formData.calibration_status} onChange={e => setFormData({...formData, calibration_status: e.target.value})}>
                            <option value="Active">Active</option>
                            <option value="Out for Calibration">Out for Calibration</option>
                            <option value="Lost">Lost</option>
                            <option value="Retired">Retired</option>
                        </select>
                    </div>
                  </div>
                  
                  <div className="mb-3 form-check">
                    <input type="checkbox" className="form-check-input" id="isMaster" checked={formData.is_master_standard} onChange={e => setFormData({...formData, is_master_standard: e.target.checked})} />
                    <label className="form-check-label fw-bold" htmlFor="isMaster">Is Master Standard? (e.g., Gauge Blocks)</label>
                    <div className="form-text">Check this if this tool is used to calibrate OTHER tools.</div>
                  </div>

                  <div className="p-3 bg-light border rounded mb-3">
                      <h6>Calibration Details</h6>
                      <div className="row">
                        <div className="col-md-6">
                            <label className="form-label">Last Calibrated Date</label>
                            <input type="date" className="form-control" value={formData.last_calibration_date} onChange={e => setFormData({...formData, last_calibration_date: e.target.value})} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Frequency (Days)</label>
                            <input type="number" className="form-control" value={formData.calibration_frequency_days} onChange={e => setFormData({...formData, calibration_frequency_days: parseInt(e.target.value)})} />
                            <small className="text-muted">Standard is 365 days (1 year)</small>
                        </div>
                      </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows="2" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Gauge</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Calibration History & Perform Modal */}
      {showHistoryModal && historyGauge && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Calibration: {historyGauge.gauge_name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
              </div>
              <div className="modal-body">
                  <div className="row">
                      
                      {/* LEFT: Perform Calibration Form */}
                      <div className="col-md-5 border-end">
                          <h6 className="mb-3 fw-bold text-primary">Record New Calibration</h6>
                          <form onSubmit={handleCalibrationSubmit}>
                              <div className="row mb-2">
                                  <div className="col-6">
                                      <label className="form-label small">Date</label>
                                      <input type="date" className="form-control form-control-sm" required value={newCalData.calibration_date} onChange={e => setNewCalData({...newCalData, calibration_date: e.target.value})} />
                                  </div>
                                  <div className="col-6">
                                      <label className="form-label small">Tech</label>
                                      <input type="text" className="form-control form-control-sm" required value={newCalData.performed_by} onChange={e => setNewCalData({...newCalData, performed_by: e.target.value})} />
                                  </div>
                              </div>
                              <div className="mb-2">
                                  <label className="form-label small">Master Standard Used</label>
                                  <select className="form-select form-select-sm" value={newCalData.standard_gauge_id} onChange={e => setNewCalData({...newCalData, standard_gauge_id: e.target.value})}>
                                      <option value="">-- Select Master Gauge --</option>
                                      {gauges.filter(g => g.is_master_standard && g.gauge_id !== historyGauge.gauge_id).map(m => (
                                          <option key={m.gauge_id} value={m.gauge_id}>{m.gauge_name} ({m.gauge_type})</option>
                                      ))}
                                  </select>
                              </div>
                              
                              {/* Dynamic Measurements Table */}
                              <div className="mb-3">
                                  <label className="form-label small fw-bold">Measurements (in inches)</label>
                                  <table className="table table-sm table-bordered mb-1" style={{fontSize: '0.85rem'}}>
                                      <thead className="table-light">
                                          <tr>
                                              <th width="30%">Nominal</th>
                                              <th width="30%">Tol (+/-)</th>
                                              <th width="30%">Actual</th>
                                              <th width="10%"></th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {measurements.map((m, i) => (
                                              <tr key={i}>
                                                  <td><input type="number" step="0.0001" className="form-control form-control-sm p-1" value={m.nominal} onChange={e => updateMeasurement(i, 'nominal', e.target.value)} /></td>
                                                  <td><input type="number" step="0.0001" className="form-control form-control-sm p-1" value={m.tolerance} onChange={e => updateMeasurement(i, 'tolerance', e.target.value)} /></td>
                                                  <td><input type="number" step="0.0001" className="form-control form-control-sm p-1" value={m.actual} onChange={e => updateMeasurement(i, 'actual', e.target.value)} /></td>
                                                  <td className="text-center"><i className="fas fa-times text-danger cursor-pointer" onClick={() => removeMeasurementRow(i)}></i></td>
                                              </tr>
                                          ))}
                                      </tbody>
                                  </table>
                                  <button type="button" className="btn btn-sm btn-outline-secondary w-100" onClick={addMeasurementRow}>+ Add Measurement Point</button>
                              </div>

                              <div className="mb-3">
                                  <label className="form-label small">Result</label>
                                  <select className="form-select form-select-sm" value={newCalData.result} onChange={e => setNewCalData({...newCalData, result: e.target.value})}>
                                      <option value="Pass">Pass</option>
                                      <option value="Fail">Fail</option>
                                  </select>
                              </div>
                              <button type="submit" className="btn btn-success w-100">Save & Generate Cert</button>
                          </form>
                      </div>

                      {/* RIGHT: History Table */}
                      <div className="col-md-7">
                          <h6 className="mb-3">History</h6>
                          <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                            <table className="table table-sm table-hover" style={{fontSize: '0.9rem'}}>
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Date</th>
                                        <th>By</th>
                                        <th>Result</th>
                                        <th>Certificate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyList.map(rec => (
                                        <tr key={rec.calibration_id} className={rec.result === 'Fail' ? 'table-danger' : ''}>
                                            <td>{rec.calibration_date}</td>
                                            <td>{rec.performed_by}</td>
                                            <td>
                                                {rec.result === 'Pass' && <span className="badge bg-success">Pass</span>}
                                                {rec.result === 'Fail' && <span className="badge bg-danger">Fail</span>}
                                            </td>
                                            <td>
                                                <a 
                                                    href={`${API_BASE}/gauges/${historyGauge.gauge_id}/calibrations/${rec.calibration_id}/certificate`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="btn btn-sm btn-outline-primary py-0"
                                                >
                                                    <i className="fas fa-file-pdf"></i> PDF
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                    {historyList.length === 0 && (
                                        <tr><td colSpan="4" className="text-center text-muted py-3">No history recorded.</td></tr>
                                    )}
                                </tbody>
                            </table>
                          </div>
                      </div>
                  </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}